import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  Chip,
  Alert,
  TextField,
  InputAdornment,
  Divider,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Category as CategoryIcon,
  Search as SearchIcon,
  Add as AddIcon,
  LocalHospital as ServiceIcon,
  TrendingUp as TrendingIcon,
  Assessment as StatsIcon,
  Schedule as ScheduleIcon,
  EventAvailable as EventIcon,
  ArrowForward as ArrowIcon,
  Close as ClearIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useServiceCategories, useServiceStatistics } from '../../hooks/useServices';
import DevOnly from '../../components/DevOnly';

const ServicesManagement = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTreatment, setSelectedTreatment] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [hasServices, setHasServices] = useState('yes'); // default hide empty categories
  const [hasAppointments, setHasAppointments] = useState('all');

  // Fetch service categories and statistics
  const { data: categoriesData, isLoading: categoriesLoading, error: categoriesError } = useServiceCategories();
  const { data: statistics, isLoading: statsLoading, error: statsError } = useServiceStatistics();

  // Debug logging
  React.useEffect(() => {
    console.log('Services Debug - categoriesData:', categoriesData);
    console.log('Services Debug - categoriesError:', categoriesError);
    console.log('Services Debug - statistics:', statistics);
    console.log('Services Debug - statsError:', statsError);
  }, [categoriesData, categoriesError, statistics, statsError]);

  const categories = categoriesData?.categories || [];
  const summary = categoriesData?.summary || {};
  const totalAppointments = summary.total_appointments || 0;

  // Treatment categorization helper function
  const getCategoryTreatmentType = (categoryName) => {
    const name = categoryName.toLowerCase();
    if (name.includes('face') || name.includes('facial')) return 'Facial Treatments';
    if (name.includes('body') || name.includes('abdomen') || name.includes('arm') || name.includes('leg')) return 'Body Treatments';
    if (name.includes('laser') || name.includes('ipl')) return 'Laser Treatments';
    if (name.includes('skin') || name.includes('aesthetic')) return 'Skin Care';
    if (name.includes('inject') || name.includes('botox') || name.includes('filler')) return 'Injectable Treatments';
    if (name.includes('consult') || name.includes('assess')) return 'Consultations';
    if (name.includes('product') || name.includes('retail')) return 'Products';
    return 'Other Treatments';
  };

  // Filter categories based on search term and selected treatment
  const filteredCategories = useMemo(() => {
    const base = categories.filter(category =>
      category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (category.code && category.code.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    const treatmentFiltered = selectedTreatment === 'all'
      ? base
      : base.filter(category => getCategoryTreatmentType(category.name) === selectedTreatment);

    const servicesFiltered = treatmentFiltered.filter(category => {
      if (hasServices === 'yes') return (category.service_count || 0) > 0;
      if (hasServices === 'no') return (category.service_count || 0) === 0;
      return true;
    });

    return servicesFiltered.filter(category => {
      if (hasAppointments === 'yes') return (category.appointment_count || 0) > 0;
      if (hasAppointments === 'no') return (category.appointment_count || 0) === 0;
      return true;
    });
  }, [categories, searchTerm, selectedTreatment, hasServices, hasAppointments]);

  // Sort categories
  const sortedCategories = useMemo(() => {
    const copy = [...filteredCategories];
    switch (sortBy) {
      case 'services_desc':
        return copy.sort((a, b) => (b.service_count || 0) - (a.service_count || 0));
      case 'appointments_desc':
        return copy.sort((a, b) => (b.appointment_count || 0) - (a.appointment_count || 0));
      case 'name':
      default:
        return copy.sort((a, b) => a.name.localeCompare(b.name));
    }
  }, [filteredCategories, sortBy]);

  const getTreatmentTypeColor = (treatmentType) => {
    const colors = {
      'Facial Treatments': 'primary',
      'Body Treatments': 'secondary',
      'Laser Treatments': 'error',
      'Skin Care': 'warning',
      'Injectable Treatments': 'success',
      'Consultations': 'info',
      'Products': 'default',
      'Other Treatments': 'default'
    };
    return colors[treatmentType] || 'default';
  };

  if (categoriesError || statsError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load service categories. Please try again later.
          <Button onClick={() => window.location.reload()} size="small" sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      </Box>
    );
  }

  const cardGridSx = {
    display: 'grid',
    gap: 3,
    gridTemplateColumns: {
      xs: 'repeat(auto-fit, minmax(240px, 1fr))',
      sm: 'repeat(auto-fit, minmax(260px, 1fr))',
      xl: 'repeat(auto-fit, minmax(280px, 1fr))'
    },
    alignItems: 'stretch'
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Service Categories
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your {summary.total_categories || 0} service categories covering {summary.total_services || 0} services ({summary.total_active_services || 0} active) and {totalAppointments} appointments
          </Typography>
        </Box>
        <DevOnly>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/services/create')}
            size="large"
          >
            Add Service
          </Button>
        </DevOnly>
      </Box>

      {/* Statistics Overview */}
      {!statsLoading && statistics && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CategoryIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" color="primary">
                    {statistics.total_categories || 0}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Total Categories
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ServiceIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="h6" color="success.main">
                    {statistics.total_services || 0}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Total Services
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrendingIcon color="warning" sx={{ mr: 1 }} />
                  <Typography variant="h6" color="warning.main">
                    {statistics.categories_with_services || 0}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Categories with Services
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <StatsIcon color="info" sx={{ mr: 1 }} />
                  <Typography variant="h6" color="info.main">
                    {statistics.categories_without_services || 0}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Empty Categories
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ScheduleIcon color="secondary" sx={{ mr: 1 }} />
                  <Typography variant="h6" color="secondary">
                    {statistics.total_appointments || 0}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Total Appointments
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <EventIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" color="primary">
                    {statistics.recent_appointments || 0}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Appointments (30 days)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      {statsLoading && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" width={80} height={28} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width={120} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search service categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search service categories"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  searchTerm ? (
                    <InputAdornment position="end">
                      <Button aria-label="Clear search" onClick={() => setSearchTerm('')} size="small" startIcon={<ClearIcon fontSize="small" />}>Clear</Button>
                    </InputAdornment>
                  ) : null
                )
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="treatment-filter-label">Treatment Type</InputLabel>
              <Select
                labelId="treatment-filter-label"
                value={selectedTreatment}
                label="Treatment Type"
                onChange={(e) => setSelectedTreatment(e.target.value)}
              >
                <MenuItem value="all">All Treatments</MenuItem>
                <MenuItem value="Facial Treatments">Facial Treatments</MenuItem>
                <MenuItem value="Body Treatments">Body Treatments</MenuItem>
                <MenuItem value="Laser Treatments">Laser Treatments</MenuItem>
                <MenuItem value="Skin Care">Skin Care</MenuItem>
                <MenuItem value="Injectable Treatments">Injectable Treatments</MenuItem>
                <MenuItem value="Consultations">Consultations</MenuItem>
                <MenuItem value="Products">Products</MenuItem>
                <MenuItem value="Other Treatments">Other Treatments</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="has-services-label">Has Services</InputLabel>
              <Select
                labelId="has-services-label"
                value={hasServices}
                label="Has Services"
                onChange={(e) => setHasServices(e.target.value)}
              >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="has-appointments-label">Has Appointments</InputLabel>
              <Select
                labelId="has-appointments-label"
                value={hasAppointments}
                label="Has Appointments"
                onChange={(e) => setHasAppointments(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="sort-by-label">Sort By</InputLabel>
              <Select
                labelId="sort-by-label"
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="name">Name (A→Z)</MenuItem>
                <MenuItem value="services_desc">Most Services</MenuItem>
                <MenuItem value="appointments_desc">Most Appointments</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Service Categories Grid */}
      {categoriesLoading ? (
        <Box sx={cardGridSx}>
          {Array.from({ length: 8 }).map((_, idx) => (
            <Box key={idx} sx={{ display: 'flex' }}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
                  width: '100%'
                }}
              >
                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Skeleton variant="text" width="70%" height={28} />
                  <Skeleton variant="text" width="40%" height={20} />
                  <Skeleton variant="rectangular" height={72} sx={{ borderRadius: 2 }} />
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      ) : sortedCategories.length === 0 ? (
        <Alert severity="info">
          {searchTerm ? 'No categories found matching your search.' : 'No service categories found.'}
        </Alert>
      ) : (
        <Box sx={cardGridSx}>
          {sortedCategories.map((category) => {
            const treatmentType = getCategoryTreatmentType(category.name);
            const treatmentColor = getTreatmentTypeColor(treatmentType);

            return (
              <Box key={category.id} sx={{ display: 'flex' }}>
                <Card
                  elevation={0}
                  sx={{
                    width: '100%',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    display: 'flex',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 16px 36px rgba(15, 23, 42, 0.12)'
                    }
                  }}
                >
                  <CardActionArea
                    onClick={() => navigate(`/services/category/${category.category_id}`)}
                    aria-label={`Open ${category.name} category`}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch'
                    }}
                  >
                    <CardContent
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            flex: 1,
                            pr: 1,
                            fontWeight: 600,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            lineHeight: 1.25
                          }}
                        >
                          {category.name}
                        </Typography>
                        <ArrowIcon color="action" fontSize="small" />
                      </Box>
                      {category.code && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Code: {category.code}
                        </Typography>
                      )}
                      <Chip
                        label={treatmentType}
                        color={treatmentColor}
                        size="small"
                        variant="outlined"
                        sx={{ mb: 2, alignSelf: 'flex-start' }}
                      />
                      <Box sx={{ flexGrow: 1 }} />
                      <Divider sx={{ my: 1.5 }} />
                      <Grid container spacing={1.5}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Services
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {category.service_count || 0}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Appointments
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {category.appointment_count || 0}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ServicesManagement;
